import React from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Box,
  Typography,
  IconButton,
  Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

function getEventIcon(eventType) {
  const iconMap = {
    person: <DirectionsRunIcon />,
    vehicle: <DirectionsCarIcon />,
    car: <DirectionsCarIcon />,
    motion: <VideocamIcon />,
  };
  return iconMap[eventType?.toLowerCase()] || <VideocamIcon />;
}

function getSeverityColor(severity) {
  const colorMap = {
    high: 'error',
    critical: 'error',
    medium: 'warning',
    low: 'info',
  };
  return colorMap[severity?.toLowerCase()] || 'info';
}

function EventNotification({
  open,
  onClose,
  event,
  autoHideDuration = 6000,
  onClick,
}) {
  if (!event) return null;

  const handleClick = () => {
    onClick?.(event);
    onClose?.();
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        severity={getSeverityColor(event.severity)}
        variant="filled"
        sx={{
          minWidth: 300,
          maxWidth: 400,
          cursor: onClick ? 'pointer' : 'default',
        }}
        onClick={handleClick}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        icon={
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'transparent',
            }}
          >
            {getEventIcon(event.event_type)}
          </Avatar>
        }
      >
        <AlertTitle>
          {event.event_type || 'Detection'} Event
        </AlertTitle>
        <Box>
          {event.camera_name && (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {event.camera_name}
            </Typography>
          )}
          {event.summary && (
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {event.summary}
            </Typography>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
}

function StatusChangeNotification({
  open,
  onClose,
  statusChange,
  autoHideDuration = 8000,
  onClick,
}) {
  if (!statusChange) return null;

  const isDown = statusChange.new_status === 'down';
  const isRecovered = statusChange.previous_status === 'down' && statusChange.new_status === 'up';

  const handleClick = () => {
    onClick?.(statusChange);
    onClose?.();
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        severity={isDown ? 'error' : isRecovered ? 'success' : 'warning'}
        variant="filled"
        sx={{
          minWidth: 300,
          maxWidth: 400,
          cursor: onClick ? 'pointer' : 'default',
        }}
        onClick={handleClick}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        icon={isDown ? <ErrorIcon /> : isRecovered ? <CheckCircleIcon /> : <WarningIcon />}
      >
        <AlertTitle>
          {isDown ? 'Site Offline' : isRecovered ? 'Site Recovered' : 'Site Status Changed'}
        </AlertTitle>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {statusChange.site_name}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {statusChange.previous_status} → {statusChange.new_status}
          </Typography>
          {isDown && statusChange.last_error && (
            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
              {statusChange.last_error}
            </Typography>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
}

export { EventNotification, StatusChangeNotification };
export default EventNotification;
